import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Col, Row, Table, Badge, Spinner, Container, Button } from 'react-bootstrap';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';
import { FaArrowLeft } from 'react-icons/fa';

function ViewPumpDomesticSO() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [salesOrder, setSalesOrder] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value || 0);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Draft': return 'secondary';
      case 'Open': return 'primary';
      case 'Completed': return 'success';
      case 'Cancelled': return 'danger';
      default: return 'warning';
    }
  };

  const getAddressString = (address) => {
    if (!address) return '-';
    return [
      address.customeraddress,
      address.city?.cityname,
      address.state?.statename,
      address.pincode,
      address.country?.countryname
    ].filter(Boolean).join(', ');
  };

  useEffect(() => {
    const fetchSalesOrder = async () => {
      try {
        const response = await axios.get(`${BASE_URL}salesOrder/findById/${id}`, {
          headers: authHeader(),
        });
        setSalesOrder(response.data);
      } catch (error) {
        console.error("Error fetching sales order:", error);
        Swal.fire("Error", "Failed to load sales order data", "error");
        navigate("/salesorderindex/1");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSalesOrder();
  }, [id]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!salesOrder) {
    return <div className="alert alert-danger text-center mt-4">Sales order not found</div>;
  }

  return (
    <Container fluid className="p-3">
      {/* Header Section */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold text-primary mb-0">SALES ORDER - VIEW</h5>
            <div>
              <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                <FaArrowLeft className="me-1" />
                Back
              </Button>
            </div>
          </div>

          <Row className="g-2">
            <Col md={3}>
              <div className="border-start border-3 border-primary ps-2">
                <small className="text-muted">SO Number</small>
                <div className="fw-bold">{salesOrder.sono || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-success ps-2">
                <small className="text-muted">SO Date</small>
                <div className="fw-bold">{formatDate(salesOrder.sodate)}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-warning ps-2">
                <small className="text-muted">Customer PO No</small>
                <div className="fw-bold">{salesOrder.customerpono || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-info ps-2">
                <small className="text-muted">Status</small>
                <div>
                  <Badge bg={getStatusBadge(salesOrder.status)} className="px-2 py-1">
                    {salesOrder.status || '-'}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Order Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Order Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Order Type</small>
              <div className="fw-bold">{salesOrder.salesordertype || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Order Category</small>
              <div className="fw-bold">{salesOrder.salesordercategory || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Quotation No</small>
              <div className="fw-bold">{salesOrder.quotationno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Quotation Date</small>
              <div className="fw-bold">{formatDate(salesOrder.quotationdate)}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Amendment No</small>
              <div className="fw-bold">{salesOrder.amendmentno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Amendment Date</small>
              <div className="fw-bold">{formatDate(salesOrder.amendmentdate)}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Customer Material Code</small>
              <div className="fw-bold">{salesOrder.customermaterialcode || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Delivery Term</small>
              <div className="fw-bold">{salesOrder.deliveryterm || '-'}</div>
            </Col>
            <Col md={12}>
              <small className="text-muted">LD Clause</small>
              <div className="fw-bold">{salesOrder.ldclause || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Customer Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Customer Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Company Name</small>
              <div className="fw-bold">{salesOrder.customer?.companyname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">GST No</small>
              <div className="fw-bold">{salesOrder.customer?.gstno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Contact Person</small>
              <div className="fw-bold">{salesOrder.contactperson || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Phone</small>
              <div className="fw-bold">{salesOrder.contactno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Email</small>
              <div className="fw-bold">{salesOrder.email || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Payment Term</small>
              <div className="fw-bold">{salesOrder.paymentterm || '-'}</div>
            </Col>
            <Col md={12}>
              <small className="text-muted">Address</small>
              <div className="fw-bold">{getAddressString(salesOrder.customeraddress)}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Branch & Warehouse Information */}
      <Row className="mb-3">
        <Col md={6}>
          <div className="shadow-sm border rounded bg-white h-100">
            <div className="bg-light py-2 px-3 border-bottom">
              <h6 className="mb-0 fw-bold">Branch Information</h6>
            </div>
            <div className="p-3">
              <Row className="g-3">
                <Col md={6}>
                  <small className="text-muted">Branch Name</small>
                  <div className="fw-bold">{salesOrder.branch?.branchname || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Branch GST</small>
                  <div className="fw-bold">{salesOrder.branch?.gstno || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Contact Person</small>
                  <div className="fw-bold">{salesOrder.branch?.contactperson || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Phone</small>
                  <div className="fw-bold">{salesOrder.branch?.phoneno || '-'}</div>
                </Col>
                <Col md={12}>
                  <small className="text-muted">Address</small>
                  <div className="fw-bold">
                    {[
                      salesOrder.branch?.address,
                      salesOrder.branch?.city?.cityname,
                      salesOrder.branch?.state?.statename,
                      salesOrder.branch?.zipcode,
                      salesOrder.branch?.country?.countryname
                    ].filter(Boolean).join(', ') || '-'}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
        <Col md={6}>
          <div className="shadow-sm border rounded bg-white h-100">
            <div className="bg-light py-2 px-3 border-bottom">
              <h6 className="mb-0 fw-bold">Warehouse Information</h6>
            </div>
            <div className="p-3">
              <Row className="g-3">
                <Col md={6}>
                  <small className="text-muted">Warehouse Name</small>
                  <div className="fw-bold">{salesOrder.warehouse?.warehousename || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Warehouse Type</small>
                  <div className="fw-bold">{salesOrder.warehouse?.warehousetype || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Contact Person</small>
                  <div className="fw-bold">{salesOrder.warehouse?.contactperson || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Phone</small>
                  <div className="fw-bold">{salesOrder.warehouse?.mobileno || '-'}</div>
                </Col>
                <Col md={12}>
                  <small className="text-muted">Address</small>
                  <div className="fw-bold">
                    {[
                      salesOrder.warehouse?.address,
                      salesOrder.warehouse?.city?.cityname,
                      salesOrder.warehouse?.state?.statename,
                      salesOrder.warehouse?.zipcode,
                      salesOrder.warehouse?.country?.countryname
                    ].filter(Boolean).join(', ') || '-'}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>

      {/* Order Items */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Order Items</h6>
        </div>
        <div className="p-0">
          <div className="table-responsive">
            <Table bordered size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="text-center fw-bold py-2">#</th>
                  <th className="fw-bold py-2">Description</th>
                  <th className="fw-bold py-2">Part No</th>
                  <th className="fw-bold py-2">HSN Code</th>
                  <th className="fw-bold py-2 text-center">Qty</th>
                  <th className="fw-bold py-2 text-center">UOM</th>
                  <th className="fw-bold py-2 text-end">Unit Price</th>
                  <th className="fw-bold py-2 text-end">Amount</th>
                  <th className="fw-bold py-2 text-center">GST %</th>
                  <th className="fw-bold py-2 text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {salesOrder.salesOrderItems?.map((item, index) => (
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>
                    <td className="fw-semibold">{item.product?.description || '-'}</td>
                    <td>{item.partno || '-'}</td>
                    <td>{item.hsnsacno || '-'}</td>
                    <td className="text-center">{item.quantity || '0'}</td>
                    <td className="text-center">{item.uom || '-'}</td>
                    <td className="text-end">{formatCurrency(item.unitrateinr)}</td>
                    <td className="text-end">{formatCurrency(item.amount)}</td>
                    <td className="text-center">{item.gst || 0}%</td>
                    <td className="text-end fw-bold">{formatCurrency(item.totalinr)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <td colSpan="9" className="text-end fw-bold py-2">Products Total:</td>
                  <td className="text-end fw-bold py-2">{formatCurrency(salesOrder.nettotalproduct)}</td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </div>
      </div>

      {/* Additional Charges */}
      {salesOrder.additionalCharges?.length > 0 && (
        <div className="shadow-sm border rounded bg-white mb-3">
          <div className="bg-light py-2 px-3 border-bottom">
            <h6 className="mb-0 fw-bold">Additional Charges</h6>
          </div>
          <div className="p-0">
            <div className="table-responsive">
              <Table bordered size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-center fw-bold py-2">#</th>
                    <th className="fw-bold py-2">Charge Type</th>
                    <th className="fw-bold py-2 text-end">Amount</th>
                    <th className="fw-bold py-2 text-center">GST %</th>
                    <th className="fw-bold py-2 text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {salesOrder.additionalCharges.map((charge, index) => (
                    <tr key={index}>
                      <td className="text-center">{index + 1}</td>
                      <td>{charge.chargetype || '-'}</td>
                      <td className="text-end">{formatCurrency(charge.chargeamount)}</td>
                      <td className="text-center">{charge.chargegst || 0}%</td>
                      <td className="text-end fw-bold">{formatCurrency(charge.chargetotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light">
                  <tr>
                    <td colSpan="4" className="text-end fw-bold py-2">Charges Total:</td>
                    <td className="text-end fw-bold py-2">{formatCurrency(salesOrder.additionalchargestotal)}</td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* Calculation Summary */}
      <Row className="mb-3">
        <Col lg={6} className="ms-auto">
          <div className="shadow-sm border rounded bg-white">
            <div className="bg-light py-2 px-3 border-bottom">
              <h6 className="mb-0 fw-bold text-center">Amount Calculation</h6>
            </div>
            <div className="p-3">
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="fw-semibold">Products Total:</span>
                <span className="fw-bold">{formatCurrency(salesOrder.nettotalproduct)}</span>
              </div>
              {salesOrder.additionalCharges?.length > 0 && (
                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                  <span className="fw-semibold">Additional Charges:</span>
                  <span className="fw-bold">{formatCurrency(salesOrder.additionalchargestotal)}</span>
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="fw-semibold">Gross Total:</span>
                <span className="fw-bold">{formatCurrency(salesOrder.grosstotal)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-2 border-top">
                <span className="fw-bold">Grand Total</span>
                <span className="fw-bold text-primary">{formatCurrency(salesOrder.grandtotalproduct)}</span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Footer Actions */}
      <div className="text-center mt-3 pt-2 border-top">
        <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
          <FaArrowLeft className="me-1" />
          Back to List
        </Button>
      </div>
    </Container>
  );
}

export default ViewPumpDomesticSO;